import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronRightIcon, 
  ChevronDownIcon, 
  WifiIcon,
  ClockIcon,
  ServerIcon,
  CpuChipIcon,
  ChartBarIcon,
  ArrowPathIcon,
  SignalIcon,
  ExclamationCircleIcon,
  CogIcon
} from '@heroicons/react/24/outline';

interface DecodedMessage {
  topic: string;
  timestamp: string;
  retained: boolean;
  size: number;
  messageType?: string;
  edgeNode?: string;
  device?: string;
  metrics?: any[];
  metricGroups?: Record<string, any[]>;
  decoded: any;
  raw?: string;
  error?: string;
}

interface TopicNode {
  name: string;
  topic?: string;
  children?: Record<string, TopicNode>;
  messageCount: number;
  lastUpdate: string;
  value?: any;
  metrics?: any[];
  metricGroups?: Record<string, any[]>;
  retained?: boolean;
}

interface Stats {
  messageCount: number;
  topicCount: number;
  retainedCount: number;
  bytesReceived: number;
  connectedClients: number;
  uptime?: number;
}

const MqttProfessional: React.FC = () => {
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState<DecodedMessage[]>([]);
  const [topicTree, setTopicTree] = useState<Record<string, TopicNode>>({});
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<TopicNode | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [filter, setFilter] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [stats, setStats] = useState<Stats>({
    messageCount: 0,
    topicCount: 0,
    retainedCount: 0,
    bytesReceived: 0,
    connectedClients: 0
  });
  
  const wsRef = useRef<WebSocket | null>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Connect to WebSocket - use direct port on dev server, proxy in production
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const isDev = window.location.port === '5173';
    const wsUrl = isDev 
      ? `${protocol}//${window.location.hostname}:3002`
      : `${protocol}//${window.location.host}/mqtt-ws`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('Connected to MQTT Decoder Service');
      setConnected(true);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'init') {
        // Initial state
        setTopicTree(data.data.topicTree || {});
        setMessages(data.data.recentMessages || []);
        setStats(data.data.stats || {});
      } else if (data.type === 'message') {
        // New message
        handleNewMessage(data.data);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('Disconnected from MQTT Decoder Service');
      setConnected(false);
    };

    // Fetch initial data via REST API as backup
    fetchTopics();
    fetchMessages();

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, []);

  useEffect(() => {
    if (autoScroll && messageContainerRef.current) {
      // Keep scrolled to top to show newest messages
      messageContainerRef.current.scrollTop = 0;
    }
  }, [messages, autoScroll]);

  const fetchTopics = async () => {
    try {
      const isDev = window.location.port === '5173';
      const apiUrl = isDev
        ? `${window.location.protocol}//${window.location.hostname}:3003/api/mqtt/topics`
        : `${window.location.protocol}//${window.location.host}/mqtt-api/api/mqtt/topics`;
      const response = await fetch(apiUrl);
      const data = await response.json();
      setTopicTree(data.topics || {});
      setStats(data.stats || {});
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const isDev = window.location.port === '5173';
      const apiUrl = isDev
        ? `${window.location.protocol}//${window.location.hostname}:3003/api/mqtt/messages?limit=100`
        : `${window.location.protocol}//${window.location.host}/mqtt-api/api/mqtt/messages?limit=100`;
      const response = await fetch(apiUrl);
      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchTopicDetails = async (topic: string) => {
    try {
      const isDev = window.location.port === '5173';
      const apiUrl = isDev
        ? `${window.location.protocol}//${window.location.hostname}:3003/api/mqtt/topic/${topic}`
        : `${window.location.protocol}//${window.location.host}/mqtt-api/api/mqtt/topic/${topic}`;
      const response = await fetch(apiUrl);
      const data = await response.json();
      setSelectedNode(data.data);
      setSelectedTopic(topic);
    } catch (error) {
      console.error('Failed to fetch topic details:', error);
    }
  };

  const handleNewMessage = (message: DecodedMessage) => {
    // Update messages list
    setMessages(prev => {
      const updated = [message, ...prev];
      if (updated.length > 200) {
        updated.pop();
      }
      return updated;
    });

    // Update stats
    setStats(prev => ({
      ...prev,
      messageCount: prev.messageCount + 1,
      bytesReceived: prev.bytesReceived + message.size
    }));
  };

  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const updated = new Set(prev);
      if (updated.has(nodeId)) {
        updated.delete(nodeId);
      } else {
        updated.add(nodeId);
      }
      return updated;
    });
  };

  const getNodeIcon = (node: TopicNode | string) => {
    const name = typeof node === 'string' ? node : node.name;
    if (name === 'spBv1.0') return <CpuChipIcon className="w-4 h-4 text-blue-500" />;
    if (name === '$SYS') return <ChartBarIcon className="w-4 h-4 text-green-500" />;
    if (name === 'Config') return <CogIcon className="w-4 h-4 text-gray-500" />;
    if (name === 'Metrics' || name === 'Production') return <ChartBarIcon className="w-4 h-4 text-purple-500" />;
    if (name === 'NDATA' || name === 'DDATA') return <SignalIcon className="w-4 h-4 text-blue-500" />;
    if (name === 'NCMD' || name === 'DCMD') return <ServerIcon className="w-4 h-4 text-orange-500" />;
    return null;
  };

  const renderTreeNode = (node: TopicNode, path: string = '', level: number = 0) => {
    const nodeId = path ? `${path}/${node.name}` : node.name;
    const isExpanded = expandedNodes.has(nodeId);
    const hasChildren = node.children && Object.keys(node.children).length > 0;
    const indent = level * 20;

    return (
      <div key={nodeId}>
        <div 
          className={`flex items-center py-1 px-2 hover:bg-gray-100 cursor-pointer rounded ${
            selectedTopic === node.topic ? 'bg-blue-50 border-l-2 border-blue-500' : ''
          }`}
          style={{ paddingLeft: `${indent + 8}px` }}
          onClick={() => {
            if (hasChildren) toggleNode(nodeId);
            if (node.topic) {
              fetchTopicDetails(node.topic);
            }
          }}
        >
          {hasChildren && (
            <span className="mr-1">
              {isExpanded ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            </span>
          )}
          {!hasChildren && <span className="mr-1 w-4" />}
          
          {getNodeIcon(node) && <span className="mr-1">{getNodeIcon(node)}</span>}
          
          <span className="font-medium mr-2">{node.name}</span>
          
          {node.messageCount > 0 && (
            <span className="ml-auto mr-2 px-2 py-0.5 text-xs bg-gray-200 rounded">
              {node.messageCount}
            </span>
          )}
          
          {node.retained && (
            <span className="px-2 py-0.5 text-xs bg-yellow-200 text-yellow-800 rounded">
              R
            </span>
          )}
          
          {node.lastUpdate && (
            <span className="ml-2 text-xs text-gray-400">
              {new Date(node.lastUpdate).toLocaleTimeString()}
            </span>
          )}
        </div>
        
        {isExpanded && hasChildren && (
          <div>
            {Object.entries(node.children!)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([key, child]) => renderTreeNode(child, nodeId, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') {
      if (Number.isInteger(value)) return value.toString();
      return value.toFixed(4);
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getMessagePreview = (msg: DecodedMessage) => {
    if (msg.metrics && msg.metrics.length > 0) {
      const preview = msg.metrics.slice(0, 3).map((m: any) => {
        const name = m.name.split('/').pop();
        const value = typeof m.value === 'number' && !Number.isInteger(m.value) 
          ? m.value.toFixed(2) 
          : m.value;
        return `${name}=${value}`;
      }).join(', ');
      return msg.metrics.length > 3 ? `${preview}, ...` : preview;
    }
    if (msg.error) return `Error: ${msg.error}`;
    if (msg.raw) return `Binary (${msg.size} bytes)`;
    if (typeof msg.decoded === 'string') return msg.decoded.substring(0, 50);
    if (msg.decoded) return JSON.stringify(msg.decoded).substring(0, 50);
    return '';
  };

  const filteredMessages = filter
    ? messages.filter(m => 
        m.topic.toLowerCase().includes(filter.toLowerCase()) ||
        JSON.stringify(m.decoded).toLowerCase().includes(filter.toLowerCase())
      )
    : messages;

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatUptime = (ms?: number) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold">MQTT Professional Monitor</h3>
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
              connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              <WifiIcon className="w-4 h-4" />
              {connected ? 'Connected' : 'Disconnected'}
            </div>
          </div>
          
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <ChartBarIcon className="w-4 h-4" />
              <span>{stats.messageCount.toLocaleString()} messages</span>
            </div>
            <div className="flex items-center gap-1">
              <ServerIcon className="w-4 h-4" />
              <span>{stats.topicCount} topics</span>
            </div>
            <div className="flex items-center gap-1">
              <ClockIcon className="w-4 h-4" />
              <span>{formatBytes(stats.bytesReceived)}</span>
            </div>
            <div className="flex items-center gap-1">
              <SignalIcon className="w-4 h-4" />
              <span>Up {formatUptime(stats.uptime)}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 px-4 pb-3">
          <input
            type="text"
            placeholder="Filter messages..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 px-3 py-1 border rounded-md text-sm"
          />
          <button
            onClick={() => {
              fetchTopics();
              fetchMessages();
            }}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={`px-3 py-1 text-sm rounded-md ${
              autoScroll ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Stay at top
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Topic Tree */}
        <div className="w-1/3 bg-white border-r overflow-auto">
          <div className="sticky top-0 p-2 bg-gray-50 border-b">
            <h4 className="font-medium text-sm">Topic Explorer</h4>
          </div>
          <div className="p-2">
            {Object.keys(topicTree).length > 0 ? (
              Object.entries(topicTree)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, node]) => renderTreeNode(node, '', 0))
            ) : (
              <div className="text-gray-400 text-sm text-center py-4">
                <ExclamationCircleIcon className="w-8 h-8 mx-auto mb-2" />
                Waiting for messages...
              </div>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex flex-col">
          {selectedNode && selectedTopic ? (
            // Topic Details View
            <div className="flex-1 bg-white p-4 overflow-auto">
              <div className="mb-4">
                <h4 className="font-semibold text-lg">{selectedTopic}</h4>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  <span>Messages: {selectedNode.messageCount}</span>
                  <span>Last Update: {new Date(selectedNode.lastUpdate).toLocaleString()}</span>
                  {selectedNode.retained && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                      Retained
                    </span>
                  )}
                </div>
              </div>

              {selectedNode.metricGroups && Object.keys(selectedNode.metricGroups).length > 0 && (
                <div className="space-y-4">
                  {Object.entries(selectedNode.metricGroups).map(([category, metrics]) => (
                    <div key={category} className="border rounded-lg">
                      <div className="bg-gray-50 px-3 py-2 font-medium flex items-center gap-2">
                        {getNodeIcon(category)}
                        {category}
                      </div>
                      <div className="p-3 space-y-2">
                        {metrics.map((metric: any, idx: number) => (
                          <div key={idx} className="flex items-center justify-between py-1">
                            <span className="text-sm font-medium">{metric.name}</span>
                            <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                              {formatValue(metric.value)}
                              {metric.type && (
                                <span className="ml-2 text-xs text-gray-500">({metric.type})</span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedNode.value && !selectedNode.metricGroups && (
                <div>
                  <h5 className="font-medium text-gray-600 mb-2">Value:</h5>
                  <pre className="p-3 bg-gray-50 rounded text-sm overflow-auto">
                    {formatValue(selectedNode.value)}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            // Message Stream View
            <div className="flex-1 overflow-auto bg-white" ref={messageContainerRef}>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Time</th>
                    <th className="text-left p-2 font-medium">Topic</th>
                    <th className="text-left p-2 font-medium">Type</th>
                    <th className="text-left p-2 font-medium">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMessages.map((msg, idx) => (
                    <tr 
                      key={idx}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        if (msg.topic) {
                          fetchTopicDetails(msg.topic);
                        }
                      }}
                    >
                      <td className="p-2 text-xs">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </td>
                      <td className="p-2 font-mono text-xs">
                        <div className="flex items-center gap-1">
                          {msg.retained && (
                            <span className="px-1 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs">
                              R
                            </span>
                          )}
                          <span className="truncate max-w-xs">{msg.topic}</span>
                        </div>
                      </td>
                      <td className="p-2">
                        {msg.messageType && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                            {msg.messageType}
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-xs text-gray-600 truncate max-w-md">
                        {getMessagePreview(msg)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MqttProfessional;